import type { Visualizer } from './types';
import { clearBg, drawHud } from './types';

/**
 * Wireframe audio terrain — wave rows extruded into a mesh.
 */
export const meshViz: Visualizer = {
  id: 'mesh',
  label: 'Wave Mesh',
  draw(frame) {
    const { ctx, w, h, wave, freq, bands, t } = frame;
    clearBg(ctx, w, h, 0.5);

    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, '#061018');
    bg.addColorStop(1, '#020508');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    const rows = 16;
    const cols = 48;
    const points: { x: number; y: number; z: number }[][] = [];

    for (let r = 0; r < rows; r++) {
      const row: { x: number; y: number; z: number }[] = [];
      const z = r / (rows - 1);
      const lag = Math.floor(z * 55 + t * 20) % wave.length;
      for (let c = 0; c < cols; c++) {
        const u = c / (cols - 1);
        const wi = Math.floor(u * (wave.length - 1));
        const s = wave[(wi + lag) % wave.length] || 0;
        const f = freq[Math.floor(u * (freq.length - 1))] / 255;
        const elev = s * (0.5 + bands.energy) + f * 0.35 * bands.bass;
        // perspective project
        const depth = 0.35 + z * 0.65;
        const x = w * 0.5 + (u - 0.5) * w * 0.95 * depth;
        const yBase = h * (0.28 + z * 0.55);
        const y = yBase - elev * h * 0.16 * (1.1 - z * 0.3);
        row.push({ x, y, z });
      }
      points.push(row);
    }

    // draw from back to front
    for (let r = 0; r < rows; r++) {
      const depth = r / (rows - 1);
      ctx.beginPath();
      for (let c = 0; c < cols; c++) {
        const p = points[r][c];
        if (c === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      }
      ctx.strokeStyle = `hsla(${168 + depth * 40 + bands.highs * 25}, 90%, ${45 + (1 - depth) * 20}%, ${0.2 + (1 - depth) * 0.55})`;
      ctx.lineWidth = 1 + (1 - depth) * 1.5 + bands.kick * 0.5;
      ctx.stroke();

      // verticals every few cols
      if (r < rows - 1) {
        ctx.beginPath();
        for (let c = 0; c < cols; c += 3) {
          const a = points[r][c];
          const b = points[r + 1][c];
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
        }
        ctx.strokeStyle = `rgba(86,240,200,${0.06 + (1 - depth) * 0.12})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }

    // horizon glow
    const hg = ctx.createLinearGradient(0, h * 0.2, 0, h * 0.5);
    hg.addColorStop(0, 'rgba(0,0,0,0)');
    hg.addColorStop(1, `rgba(86,240,200,${0.04 + bands.energy * 0.06})`);
    ctx.fillStyle = hg;
    ctx.fillRect(0, h * 0.2, w, h * 0.35);

    drawHud(frame);
  },
};
